import { useState, useCallback } from 'react'
import type { Tasting, Taster, Wine } from '../types'
import { CURRENT_TASTING_VERSION } from '../types'

const STORAGE_KEY = 'lwwt-tasting'

function migrateTasting(data: Record<string, unknown>): Tasting {
  let version = (data.version as number) ?? 1

  // v1 → v2: Add wineAssignments (wines auto-assigned in order)
  if (version < 2) {
    const wines = data.wines as Wine[]
    data.wineAssignments = wines.map((_, i) => i)
    version = 2
  }

  // v2 → v3: Rename id→_id on tasting, id→_key on tasters, add _key to wines, add hasUnsavedChanges
  if (version < 3) {
    // Rename tasting.id → tasting._id
    if ('id' in data && !('_id' in data)) {
      data._id = data.id
      delete data.id
    }
    // Rename taster.id → taster._key
    const tasters = data.tasters as Record<string, unknown>[]
    if (tasters) {
      for (const taster of tasters) {
        if ('id' in taster && !('_key' in taster)) {
          taster._key = taster.id
          delete taster.id
        }
      }
    }
    // Add _key to wines
    const wines = data.wines as Record<string, unknown>[]
    if (wines) {
      for (const wine of wines) {
        if (!('_key' in wine)) {
          wine._key = crypto.randomUUID()
        }
      }
    }
    // Add hasUnsavedChanges
    if (!('hasUnsavedChanges' in data)) {
      data.hasUnsavedChanges = false
    }
    version = 3
  }

  data.version = version
  return data as Tasting
}

function loadTasting(): Tasting | null {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return null
  try {
    const parsed = JSON.parse(stored) as Record<string, unknown>
    return migrateTasting(parsed)
  } catch {
    return null
  }
}

function persistTasting(tasting: Tasting | null): void {
  if (tasting === null) {
    localStorage.removeItem(STORAGE_KEY)
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasting))
  }
}

export function useTasting() {
  const [tasting, setTastingState] = useState<Tasting | null>(() => loadTasting())

  const setTasting = useCallback((newTasting: Tasting | null) => {
    setTastingState(newTasting)
    persistTasting(newTasting)
  }, [])

  const setTastingWithDirty = useCallback(
    (newTasting: Tasting) => {
      setTasting({ ...newTasting, hasUnsavedChanges: true })
    },
    [setTasting],
  )

  const createTasting = useCallback(
    (theme: string, wines: Omit<Wine, '_key'>[], host?: string, date?: string) => {
      const newTasting: Tasting = {
        version: CURRENT_TASTING_VERSION,
        _id: crypto.randomUUID(),
        theme,
        host: host || undefined,
        date: date || undefined,
        wines: wines.map((w) => ({ ...w, _key: crypto.randomUUID() })),
        tasters: [],
        wineAssignments: wines.map(() => null), // Start unassigned
        hasUnsavedChanges: false,
      }
      setTasting(newTasting)
    },
    [setTasting],
  )

  const updateTasting = useCallback(
    (theme: string, wines: Omit<Wine, '_key'>[], host?: string, date?: string) => {
      if (!tasting) return
      // Match existing wine keys by index where possible, generate new ones otherwise
      const keyedWines: Wine[] = wines.map((w, i) => ({
        ...w,
        _key: tasting.wines[i]?._key ?? crypto.randomUUID(),
      }))
      const updatedTasters = tasting.tasters.map((taster) => {
        // Adjust array length to match wines
        const newScores = wines.map((_, i) => taster.scores[i] ?? null)
        return { ...taster, scores: newScores }
      })
      // Adjust wineAssignments length to match wines
      const newAssignments = wines.map((_, i) => {
        const existing = tasting.wineAssignments[i]
        // Keep assignment only if the wineIndex is still valid
        if (existing !== null && existing < wines.length) {
          return existing
        }
        return null
      })
      setTastingWithDirty({
        ...tasting,
        theme,
        host: host || undefined,
        date: date || undefined,
        wines: keyedWines,
        tasters: updatedTasters,
        wineAssignments: newAssignments,
      })
    },
    [tasting, setTastingWithDirty],
  )

  const resetTasting = useCallback(() => {
    setTasting(null)
  }, [setTasting])

  const addTaster = useCallback(
    (name: string) => {
      if (!tasting) return
      const newTaster: Taster = {
        _key: crypto.randomUUID(),
        name,
        scores: tasting.wineAssignments.map(() => null), // One score per letter slot
      }
      setTastingWithDirty({
        ...tasting,
        tasters: [...tasting.tasters, newTaster],
      })
    },
    [tasting, setTastingWithDirty],
  )

  const updateTaster = useCallback(
    (_key: string, updates: Partial<Pick<Taster, 'name' | 'scores'>>) => {
      if (!tasting) return
      setTastingWithDirty({
        ...tasting,
        tasters: tasting.tasters.map((t) => (t._key === _key ? { ...t, ...updates } : t)),
      })
    },
    [tasting, setTastingWithDirty],
  )

  const removeTaster = useCallback(
    (_key: string) => {
      if (!tasting) return
      setTastingWithDirty({
        ...tasting,
        tasters: tasting.tasters.filter((t) => t._key !== _key),
      })
    },
    [tasting, setTastingWithDirty],
  )

  const setTasters = useCallback(
    (tasters: Taster[]) => {
      if (!tasting) return
      setTastingWithDirty({
        ...tasting,
        tasters,
      })
    },
    [tasting, setTastingWithDirty],
  )

  const assignWine = useCallback(
    (letterIndex: number, wineIndex: number | null) => {
      if (!tasting) return
      const newAssignments = [...tasting.wineAssignments]
      // If wineIndex is already assigned elsewhere, unassign it first
      if (wineIndex !== null) {
        const existingLetterIndex = newAssignments.indexOf(wineIndex)
        if (existingLetterIndex !== -1 && existingLetterIndex !== letterIndex) {
          newAssignments[existingLetterIndex] = null
        }
      }
      newAssignments[letterIndex] = wineIndex
      setTastingWithDirty({
        ...tasting,
        wineAssignments: newAssignments,
      })
    },
    [tasting, setTastingWithDirty],
  )

  const markSaved = useCallback(() => {
    if (!tasting) return
    setTasting({ ...tasting, _type: 'tasting', hasUnsavedChanges: false })
  }, [tasting, setTasting])

  return {
    tasting,
    createTasting,
    updateTasting,
    resetTasting,
    addTaster,
    updateTaster,
    removeTaster,
    setTasters,
    assignWine,
    markSaved,
  }
}
